import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { OfferSchema } from "../schemas/offer";
import { customersApiSchema } from "./customersService";
import { usersApiSchema } from "./usersApi";

export const CONTACTS_QUERY_KEY = "contacts";

export const ContactDetailsSchema = z.object({
    id: z.string(),
    way: z.string(),
    subject: z.string(),
    date: z.string(),
    note: z.string(),
    seller: usersApiSchema.seller,
    person: customersApiSchema.person,
    products: z
        .array(
            z.object({
                id: z.string(),
                name_full: z.string(),
                product_number: z.string(),
                footage: z.number(),
                floor: z.string().or(z.number()),
                investment: z.object({
                    id: z.string(),
                    name: z.string(),
                }),
            }),
        )
        .optional(),
    offer: OfferSchema.optional(),
});

export const contactsApiSchema = {
    contact: ContactDetailsSchema,
};

export function useContacts(searchParams, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: [
            CONTACTS_QUERY_KEY,
            searchParams ? Object.fromEntries(searchParams.entries()) : {},
        ],
        queryFn: () => {
            return contactsApi.getContacts(axiosPriv, searchParams);
        },
    });
}

export function useContactChannelsReport(searchParams, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: [
            "contact-channel-report",
            searchParams ? Object.fromEntries(searchParams.entries()) : {},
        ],
        queryFn: () => {
            return contactsApi.getContactChannelReport(axiosPriv, searchParams);
        },
    });
}

export function useCreateContactMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ["agreement-overdue-payments"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (data) => {
            return contactsApi.createContact(axiosPriv, data);
        },
    });
}

export function useEditContactMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({id, ...data}) => {
            return contactsApi.editContact(axiosPriv, id, data);
        },
    });
}

export function useContactSubjects(options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["contactSubjects"],
        queryFn: () => {
            return contactsApi.getContactSubjects(axiosPriv);
        },
    });
}

export function useContactsForPerson(personId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["contacts", "person", personId],
        queryFn: () => {
            return contactsApi.getContactsForPerson(axiosPriv, personId);
        },
        enabled: !!personId,
    });
}

export const contactsApi = {
    getContacts: (axios, params) => {
        return axios.get(apiUrls.sales.contacts.all, { params }).then((response) => response.data);
    },

    getContactChannelReport: (axios, params) => {
        return axios.get(apiUrls.sales.contacts.channelReport, { params }).then((response) => response.data);
    },

    getContactsForPerson: (axios, personId) => {
        return axios
            .get(apiUrls.sales.contacts.forPerson(personId))
            .then((response) => response.data);
    },

    createContact: (axios, data) => {
        return axios.post(apiUrls.sales.contacts.contacts, data).then((response) => response.data);
    },

    editContact: (axios, contactId, body) => {
        return axios.patch(
            apiUrls.sales.contacts.editContact(contactId),
            body,
        ).then((response) => response.data);
    },

    getContactSubjects: (axios) => {
        return axios.get(apiUrls.sales.contacts.subjects).then((response) => response.data);
    },
};
